import {
  PolicyChatConversation,
  PolicyChatConversationSummary,
  PolicyChatErrorType,
  SendPolicyChatMessageResponse,
  StartPolicyChatResponse,
} from '@enview/interface/types/policyChat/PolicyChat';
import base, { ApiTag, errorHasData } from './base';

const CONVERSATION_LIST_TAG = { type: ApiTag.POLICY_CHAT, id: 'LIST' };

interface ListConversationsParams {
  limit: number;
  offset?: number;
}

interface SendMessageParams {
  conversationId: string;
  message: string;
}

// RTK Query passes an undefined error for successful mutations, which
// errorHasData cannot take.
const isReadOnlyError = (error: unknown): boolean =>
  !!error &&
  errorHasData(error) &&
  error.data?.errorType === PolicyChatErrorType.ConversationReadOnly;

const policyChatAPI = base.injectEndpoints({
  endpoints: (build) => ({
    getPolicyChatConversations: build.query<
      PolicyChatConversationSummary[],
      ListConversationsParams
    >({
      query: ({ limit, offset = 0 }) => ({
        url: '/v2/policy-chat/conversations',
        method: 'GET',
        params: { limit, offset },
      }),
      providesTags: [CONVERSATION_LIST_TAG],
    }),
    getPolicyChatConversation: build.query<PolicyChatConversation, string>({
      query: (conversationId) => ({
        url: `/v2/policy-chat/conversations/${conversationId}`,
        method: 'GET',
      }),
      providesTags: (result, error, conversationId) => [
        { type: ApiTag.POLICY_CHAT, id: conversationId },
      ],
    }),
    startPolicyChat: build.mutation<StartPolicyChatResponse, string>({
      query: (message) => ({
        url: '/v2/policy-chat/conversations',
        method: 'POST',
        data: { message },
      }),
      invalidatesTags: [CONVERSATION_LIST_TAG],
    }),
    sendPolicyChatMessage: build.mutation<
      SendPolicyChatMessageResponse,
      SendMessageParams
    >({
      query: ({ conversationId, message }) => ({
        url: `/v2/policy-chat/conversations/${conversationId}/messages`,
        method: 'POST',
        data: { message },
      }),
      // Append the new turn to the cached conversation so the thread does not
      // refetch every message after each answer.
      onQueryStarted: async ({ conversationId }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            policyChatAPI.util.updateQueryData(
              'getPolicyChatConversation',
              conversationId,
              (draft) => {
                Object.assign(draft, data.conversation);
                draft.messages.push(...data.messages);
              },
            ),
          );
        } catch {
          // A failed turn saves nothing on the server, so the cache stays as is.
        }
      },
      invalidatesTags: (result, error, { conversationId }) =>
        isReadOnlyError(error)
          ? [CONVERSATION_LIST_TAG, { type: ApiTag.POLICY_CHAT, id: conversationId }]
          : [CONVERSATION_LIST_TAG],
    }),
  }),
  overrideExisting: false,
});

export default policyChatAPI;
